import { useState, FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d6rjo6c4k19g20dvrmsg';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      setError('Please fill in all required fields.');
      return;
    }

    if (formData.message.length > 500) {
      setError('Message must be 500 characters or less.');
      return;
    }

    setSubmitting(true);

    try {
      const body = new URLSearchParams();
      body.append('name', formData.name);
      body.append('email', formData.email);
      body.append('message', formData.message);

      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (res.ok) {
        setSubmitted(true);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-white rounded-2xl p-12 text-center space-y-6">
        <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
          <i className="ri-check-line text-primary text-4xl"></i>
        </div>
        <h3 className="text-3xl font-black text-black">Thanks for reaching out. We'll get back to you within 24 hours. Looking forward to connecting!</h3>
      </div>
    );
  }

  return (
    <form
      id="contact-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="bg-white rounded-2xl p-8 lg:p-10 space-y-6"
    >
      <h3 className="text-2xl font-black text-black mb-2">Send a message</h3>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label htmlFor="name" className="text-xs uppercase tracking-wider text-gray-400 font-bold">
            Name <span className="text-primary">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Your full name"
            required
            className="w-full border-b-2 border-gray-200 focus:border-primary outline-none py-3 text-sm font-medium text-black placeholder:text-gray-300 transition-colors bg-transparent"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="email" className="text-xs uppercase tracking-wider text-gray-400 font-bold">
            Email <span className="text-primary">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="you@company.com"
            required
            className="w-full border-b-2 border-gray-200 focus:border-primary outline-none py-3 text-sm font-medium text-black placeholder:text-gray-300 transition-colors bg-transparent"
          />
        </div>
      </div>

      <div className="space-y-2">
        <label htmlFor="message" className="text-xs uppercase tracking-wider text-gray-400 font-bold">
          Message <span className="text-primary">*</span>
        </label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          placeholder="Tell us about your challenge or project..."
          required
          maxLength={500}
          rows={5}
          className="w-full border-b-2 border-gray-200 focus:border-primary outline-none py-3 text-sm font-medium text-black placeholder:text-gray-300 transition-colors bg-transparent resize-none"
        />
        <p className="text-xs text-gray-300 font-medium text-right">{formData.message.length}/500</p>
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 text-sm font-medium px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="bg-primary text-white px-10 py-4 rounded-full hover:bg-primary/90 transition-all duration-300 text-sm font-bold uppercase tracking-wider inline-flex items-center gap-3 whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {submitting ? 'Sending...' : 'Send Message'}
        <i className="ri-arrow-right-line text-lg"></i>
      </button>
    </form>
  );
}
